import React, {
  type ComponentType,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Theme } from '@mui/material/styles';
import type { LayoutUserAccountProps } from '@core/components/layout/LayoutUserAccount';
import { getSidebarWidth } from '@core/constants/config';
import { useAuth } from '@core/contexts/AuthContext';
import { useTheme } from '@core/contexts/ThemeContext';
import { useIsMobile } from '@core/hooks/useIsMobile';
import { NavItem } from '@core/interfaces';
import { filterNavItemsByRole, getRouteConfig } from '@core/utils/routes';

export type WebLayoutVariant = 'webone' | 'webtwo';
export type LayoutVariant = 'mobile' | WebLayoutVariant;

export type LayoutExpandedLogo = {
  logoHeight: number;
  logoWidth?: number;
  component: ComponentType<{
    sidebarOpen: boolean;
    logoHeight: number;
  }>;
};

export type LayoutContextValue = {
  theme: Theme;
  layout: LayoutVariant;
  isMobile: boolean;
  isWebOne: boolean;
  isWebTwo: boolean;
  sidebarOpen: boolean;
  sidebarWidth: number;
  toggleSidebar: () => void;
  showBreadcrumbs: boolean;
  visibleNavItems: NavItem[];
  routeConfig: ReturnType<typeof getRouteConfig>;
  userAccount: LayoutUserAccountProps;
  expandedLogo?: LayoutExpandedLogo;
};

const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export interface LayoutProviderProps {
  navItems: NavItem[];
  webVariant?: WebLayoutVariant;
  showBreadcrumbs?: boolean;
  expandedLogo?: LayoutExpandedLogo;
  children: ReactNode;
}

export type LayoutProps = Omit<LayoutProviderProps, 'children'>;

export const LayoutProvider: React.FC<LayoutProviderProps> = ({
  navItems,
  webVariant = 'webone',
  showBreadcrumbs = true,
  expandedLogo,
  children,
}) => {
  const { user, logout } = useAuth();
  const { mode, theme, toggleTheme } = useTheme();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [profileOpen, setProfileOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const layout: LayoutVariant = isMobile ? 'mobile' : webVariant;

  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  const toggleSidebar = useCallback(() => {
    setSidebarOpen((open) => !open);
  }, []);

  const visibleNavItems = useMemo(
    () => (user ? filterNavItemsByRole(navItems, user.role) : navItems),
    [navItems, user]
  );

  const routeConfig = useMemo(
    () => getRouteConfig(visibleNavItems),
    [visibleNavItems]
  );

  const sidebarWidth = getSidebarWidth(sidebarOpen);

  const userAccount = useMemo(
    (): LayoutUserAccountProps => ({
      user,
      profileOpen,
      onProfileClick: () => setProfileOpen(true),
      onCloseProfile: () => setProfileOpen(false),
      changePasswordOpen,
      onChangePasswordClick: () => setChangePasswordOpen(true),
      onCloseChangePassword: () => setChangePasswordOpen(false),
      isDarkMode: mode === 'dark',
      onThemeToggle: toggleTheme,
      onLogout: logout,
    }),
    [user, profileOpen, changePasswordOpen, mode, toggleTheme, logout]
  );

  const value: LayoutContextValue = {
    theme,
    layout,
    isMobile,
    isWebOne: layout === 'webone',
    isWebTwo: layout === 'webtwo',
    sidebarOpen,
    sidebarWidth,
    toggleSidebar,
    showBreadcrumbs,
    visibleNavItems,
    routeConfig,
    userAccount,
    expandedLogo,
  };

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useAppLayout = (): LayoutContextValue => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useAppLayout must be used within a LayoutProvider');
  }
  return context;
};
